import React from 'react';
import { motion } from 'framer-motion';
import {
  Download,
  FileText,
  Users,
  Shield,
  Heart,
  BookOpen,
  CheckCircle
} from 'lucide-react';

const Resources = () => {
  const resourceCategories = [
    {
      title: "For Parents",
      icon: Users,
      color: "primary",
      resources: [
        {
          name: "Member Handbook (EN/FR)",
          type: "PDF",
          description: "Explains coverage, exclusions, and the AI + Western telehealth workflow (school vitals → AI pre‑diagnosis → doctor review → clinic only for procedures)",
          size: "2.3 MB"
        },
        {
          name: "Coverage Summary Sheet",
          type: "PDF",
          description: "One‑page overview of outpatient, hospitalization, prevention, and AI + Western telehealth (with referral to clinics only for procedures)",
          size: "1.1 MB"
        },
        {
          name: "Enrollment Guide",
          type: "PDF",
          description: "Step-by-step process, required documents, and payment options",
          size: "0.8 MB"
        },
        {
          name: "AI + Telehealth Quick Guide",
          type: "PDF",
          description: "How school vitals and symptoms feed AI triage, how Western doctors review and send care plans, and how to do phone/video follow‑ups",
          size: "0.6 MB"
        }
      ]
    },
    {
      title: "For Schools",
      icon: BookOpen,
      color: "accent",
      resources: [
        {
          name: "Enrollment Toolkit",
          type: "ZIP",
          description: "Complete package for school enrollment implementation",
          size: "15.2 MB"
        },
        {
          name: "Parent Information Posters",
          type: "PDF",
          description: "Educational materials for parents and families",
          size: "3.4 MB"
        },
        {
          name: "Consent Forms",
          type: "PDF",
          description: "Required forms for student enrollment",
          size: "0.5 MB"
        },
        {
          name: "Health Education Curriculum",
          type: "PDF",
          description: "Age-appropriate health lessons and activities",
          size: "8.7 MB"
        }
      ]
    },
    {
      title: "For Providers",
      icon: Shield,
      color: "secondary",
      resources: [
        {
          name: "Claims Processing Guide",
          type: "PDF",
          description: "Step-by-step guide to submitting and processing claims",
          size: "2.1 MB"
        },
        {
          name: "Fee Schedule",
          type: "PDF",
          description: "Current fee schedule for covered services",
          size: "1.3 MB"
        },
        {
          name: "Quality Checklist",
          type: "PDF",
          description: "Quality standards and compliance requirements",
          size: "0.9 MB"
        },
        {
          name: "Provider Portal Guide",
          type: "PDF",
          description: "How to use the digital provider portal",
          size: "1.8 MB"
        }
      ]
    },
    {
      title: "For Investors/CSR",
      icon: Heart,
      color: "primary",
      resources: [
        {
          name: "Investor Brief",
          type: "PDF",
          description: "Comprehensive overview of investment opportunity",
          size: "4.2 MB"
        },
        {
          name: "Pitch Deck (English)",
          type: "PDF",
          description: "Presentation for investor meetings and presentations",
          size: "12.5 MB"
        },
        {
          name: "Pitch Deck (French)",
          type: "PDF",
          description: "Présentation pour les réunions d'investisseurs",
          size: "12.5 MB"
        },
        {
          name: "Impact Methodology",
          type: "PDF",
          description: "How we measure and report social impact",
          size: "3.1 MB"
        }
      ]
    },
    {
      title: "Policies & Compliance",
      icon: FileText,
      color: "accent",
      resources: [
        {
          name: "Insurance Policy & Guidelines",
          type: "PDF",
          description: "Complete insurance policy terms and conditions",
          size: "5.8 MB"
        },
        {
          name: "Risk & Compliance Overview",
          type: "PDF",
          description: "Risk management and compliance framework",
          size: "2.4 MB"
        },
        {
          name: "Data Protection Policy",
          type: "PDF",
          description: "How we protect personal and health data",
          size: "1.7 MB"
        },
        {
          name: "Safeguarding Policy",
          type: "PDF",
          description: "Child protection and safeguarding procedures",
          size: "2.9 MB"
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="section-padding bg-gradient-to-br from-primary-600 to-accent-600 text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Resources & Downloads</h1>
            <p className="text-xl md:text-2xl text-primary-100">
              Everything you need to know about AMHAS
            </p>
          </motion.div>
        </div>
      </section>

      {/* Resource Categories */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Download Resources</h2>
            <p className="section-subtitle">
              Find the information you need by category
            </p>
          </motion.div>

          <div className="space-y-12">
            {resourceCategories.map((category, categoryIndex) => {
              const Icon = category.icon;
              return (
                <motion.div
                  key={category.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-8"
                >
                  <div className="flex items-center mb-8">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center mr-4 ${category.color === 'primary' ? 'bg-primary-100' :
                        category.color === 'accent' ? 'bg-accent-100' :
                          'bg-secondary-100'
                      }`}>
                      <Icon size={32} className={
                        category.color === 'primary' ? 'text-primary-600' :
                          category.color === 'accent' ? 'text-accent-600' :
                            'text-secondary-600'
                      } />
                    </div>
                    <h3 className="text-3xl font-bold text-gray-900">{category.title}</h3>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {category.resources.map((resource, resourceIndex) => (
                      <motion.div
                        key={resource.name}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.4, delay: resourceIndex * 0.1 }}
                        viewport={{ once: true }}
                        className="flex items-center space-x-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200"
                      >
                        <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center shadow-sm">
                          <Download size={24} className="text-primary-600" />
                        </div>

                        <div className="flex-1">
                          <h4 className="text-lg font-semibold text-gray-900 mb-1">
                            {resource.name}
                          </h4>
                          <p className="text-gray-600 text-sm mb-2">
                            {resource.description}
                          </p>
                          <div className="flex items-center space-x-4 text-xs text-gray-500">
                            <span className="px-2 py-1 bg-gray-200 rounded">
                              {resource.type}
                            </span>
                            <span>{resource.size}</span>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Quick Access */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="section-title">Quick Access</h2>
            <p className="section-subtitle">
              Most frequently downloaded resources
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                title: "Member Handbook",
                description: "Complete guide for parents and families",
                downloads: "2,500+",
                icon: BookOpen
              },
              {
                title: "Enrollment Toolkit",
                description: "Everything schools need to get started",
                downloads: "1,200+",
                icon: Users
              },
              {
                title: "Investor Brief",
                description: "Investment opportunity overview",
                downloads: "800+",
                icon: Heart
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="card p-8 text-center"
                >
                  <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Icon size={32} className="text-primary-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 mb-4">{item.description}</p>
                  <div className="text-sm text-primary-600 font-semibold">
                    {item.downloads} downloads
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Contact for More */}
      <section className="section-padding bg-primary-600 text-white">
        <div className="container-custom text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl font-bold mb-4">Need More Information?</h2>
            <p className="text-xl text-primary-100 mb-8 max-w-3xl mx-auto">
              Can't find what you're looking for? Contact us for additional resources or customized materials.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <a href="/contact-form" className="btn-secondary">
                Email Us
              </a>
              <a href="tel:+2204040966" className="btn-secondary">
                Call Us
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
